/// Client-side image preparation for the two data-URL upload endpoints (user
/// avatar, org image). Everything — SVG included — is rasterised onto a
/// canvas, so the server's allowlist stays raster-only and no XML reaches
/// storage.

import { formatBytes } from "@/lib/format";

export const IMAGE_MAX_EDGE = 256;
export const IMAGE_QUALITY = 0.8;
// Caps the CHOSEN file; the server's 1 MB applies to the 256 px copy that is sent.
export const IMAGE_MAX_FILE_SIZE = 5_000_000;
export const IMAGE_LIMITS = "JPEG, PNG, WebP or SVG, up to 5 MB";
export const IMAGE_ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/svg+xml",
] as const;
export const IMAGE_ACCEPT_STRING = IMAGE_ACCEPTED_TYPES.join(",");

export function imageRefusal(file: File): string | null {
  if (
    !IMAGE_ACCEPTED_TYPES.includes(
      file.type as (typeof IMAGE_ACCEPTED_TYPES)[number],
    )
  )
    return `${file.name} isn't a JPEG, PNG, WebP or SVG image`;
  if (file.size > IMAGE_MAX_FILE_SIZE)
    return `${file.name} is ${formatBytes(file.size)}. Images go up to 5 MB.`;
  return null;
}

export function resizeImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);

      if (!img.width || !img.height) {
        return reject(new Error("Failed to load image"));
      }

      const scale = Math.min(
        IMAGE_MAX_EDGE / img.width,
        IMAGE_MAX_EDGE / img.height,
        1,
      );
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas not supported"));

      ctx.drawImage(img, 0, 0, w, h);
      const outputType =
        file.type === "image/svg+xml"
          ? "image/png"
          : file.type === "image/png" || file.type === "image/webp"
            ? file.type
            : "image/jpeg";
      resolve(canvas.toDataURL(outputType, IMAGE_QUALITY));
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image"));
    };
    img.src = URL.createObjectURL(file);
  });
}
